<template>
  <div class="center-box">
    <div class="searchBox">
      <a-space class="mr-10">
        <a-input class="keyword" allowClear v-model:value="queryParams.name" @pressEnter="changePage"
          placeholder="请输入模型名称" />
      </a-space>
      <a-button type="default" @click="changePage" class="mr-10">查询</a-button>
      <a-button class="ant-btn-secondary" @click="addModel">新增模型</a-button>
    </div>

    <a-modal v-model:open="uploadModalVisible" title="上传模型" centered :maskClosable="false">
      <a-form ref="uploadFormRef" :model="uploadForm" :rules="uploadRules" :label-col="{ span: 6 }">
        <a-form-item name="name" label="模型名称" required>
          <a-input v-model:value="uploadForm.name" placeholder="请输入模型名称" />
        </a-form-item>
        <a-form-item name="label" label="模型标签" required>
          <a-input v-model:value="uploadForm.label" placeholder="请输入模型标签，如：摊贩" />
        </a-form-item>
        <a-form-item name="version" label="版本号" required>
          <a-input v-model:value="uploadForm.version" placeholder="请输入版本号，如：1.0" />
        </a-form-item>
        <a-form-item name="status" label="状态" required>
          <a-select v-model:value="uploadForm.status" placeholder="请选择状态" style="width: 100%">
            <a-select-option value="1">正常</a-select-option>
            <a-select-option value="0">停用</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="模型文件" required>
          <a-upload :multiple="false" :before-upload="beforeUpload" :show-upload-list="true" :file-list="fileList"
            :remove="removeFile">
            <a-button type="default">
              <UploadOutlined />
              选择模型文件
            </a-button>
          </a-upload>
        </a-form-item>
      </a-form>
      <template #footer>
        <a-button key="back" class="ant-btn-secondary" @click="onUploadCancel">取消</a-button>
        <a-button key="submit" type="default" :loading="uploadLoading" @click="handleUpload">确定</a-button>
      </template>
    </a-modal>

    <a-table :data-source="modelList" align="center" class="mt10" :pagination="paginationProp" row-key="id"
      @change="refreshData">
      <a-table-column align="center" key="name" title="模型名称" data-index="name" />
      <a-table-column align="center" key="version" title="版本号" data-index="version" />
      <a-table-column align="center" key="update_time" title="最近训练时间" data-index="update_time">
        <template #default="{ record }">
          {{ record.update_time }}
        </template>
      </a-table-column>
      <a-table-column align="center" key="online_time" title="上线时间" data-index="online_time">
        <template #default="{ record }">
          {{ record.online_time }}
        </template>
      </a-table-column>
      <a-table-column align="center" key="status" title="状态" data-index="status">
        <template #default="{ record }">
          {{ record.status === '1' ? '正常' : '停用' }}
        </template>
      </a-table-column>
      <a-table-column align="center" key="create_time" title="创建时间" data-index="create_time">
        <template #default="{ record }">
          {{ record.create_time }}
        </template>
      </a-table-column>
      <a-table-column align="center" key="action" title="操作">
        <template #default="{ record }">
          <a-button type="text" @click="handleDelete(record)">删除模型</a-button>
        </template>
      </a-table-column>
    </a-table>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, reactive, h } from 'vue'
import { message, Modal } from 'ant-design-vue'
import { UploadOutlined } from '@ant-design/icons-vue'
import { getAlgorithmModelListPageVo, uploadModel, deleteModel } from '/@/api/manage'
import { ELocalStorageKey } from '/@/types'

const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
const queryParams = ref({
  page: 1,
  size: 10,
  name: '',
})
const modelList = ref([])

const paginationProp = reactive({
  showQuickJumper: false,
  showSizeChanger: false,
  page_size: 10,
  current: 1,
  total: 0
})
const algorithmTypeOptions = ref([])

// 上传模型相关
const uploadModalVisible = ref(false)
const uploadLoading = ref(false)
const uploadFormRef = ref()
const uploadForm = reactive({
  name: '',
  label: '',
  version: '1.0',
  status: '1'
})
const uploadRules = {
  name: [{ required: true, message: '请输入模型名称' }],
  label: [{ required: true, message: '请输入模型标签' }],
  version: [{ required: true, message: '请输入版本号' }],
  status: [{ required: true, message: '请选择状态' }]
}

interface FileItem {
  uid: string
  name?: string
  status?: string
}
const fileList = ref<FileItem[]>([])

onMounted(() => {
  getList()
})

function changePage() {
  queryParams.value.page = 1
  getList()
}
function refreshData(page: any) {
  queryParams.value.page = page.current
  getList()
}
function getList() {
  getAlgorithmModelListPageVo(queryParams.value).then(res => {
    if (res.code === 0) {
      const data = res.data || {}
      const pagination = data.pagination || {}
      modelList.value = data.list || []
      paginationProp.total = Number(pagination.total || 0)
      paginationProp.current = Number(pagination.page || queryParams.value.page || 1)
    }
  })
}

function handleDelete(record: { id: string | number; name?: string }) {
  Modal.confirm({
    title: h('div', { style: 'color:#ffffff;' }, `确定要删除模型${record.name ?? record.id}吗？`),
    okText: '确定',
    okType: 'default',
    okButtonProps: { type: 'default' } as Record<string, unknown>,
    cancelText: '取消',
    cancelButtonProps: { class: 'ant-btn-secondary' } as Record<string, unknown>,
    onOk() {
      return deleteModel({ id: record.id }).then(res => {
        if (res.code === 0) {
          message.success('删除成功')
          getList()
        } else {
          message.error(res.message || '删除失败')
        }
      })
    }
  })
}

function addModel() {
  uploadModalVisible.value = true
}

function beforeUpload(file: FileItem) {
  fileList.value = [file]
  return false // 阻止自动上传
}

function removeFile() {
  fileList.value = []
}

function onUploadCancel() {
  uploadFormRef.value?.resetFields()
  fileList.value = []
  uploadForm.name = ''
  uploadForm.label = ''
  uploadForm.version = '1.0'
  uploadForm.status = '1'
  uploadLoading.value = false
  uploadModalVisible.value = false
}

async function handleUpload() {
  if (fileList.value.length === 0) {
    message.error('请选择模型文件')
    return
  }
  try {
    await uploadFormRef.value?.validate()
    const fileItem = fileList.value[0] as any
    const file = fileItem.originFileObj || fileItem
    const formData = new FormData()
    formData.append('file', file, file.name)
    formData.append('name', uploadForm.name)
    formData.append('label', uploadForm.label)
    formData.append('status', uploadForm.status)
    formData.append('version', uploadForm.version)

    uploadLoading.value = true
    const res = await uploadModel(formData)
    if (res.code === 0) {
      message.success('模型上传成功')
      onUploadCancel()
      getList()
    } else {
      message.error(res.message || '上传失败')
    }
  } catch (e) {
    // 表单校验失败
  } finally {
    uploadLoading.value = false
  }
}
</script>

<style lang="scss" scoped></style>
